import {
  REFRACTORIES_WITH_CUSTOMIZATION_QUERY_KEY,
  type ErrorResponse
} from '@domain/refractory-product-catalog'
import {ApiClient} from '@hconnect/apiclient'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import type {AxiosError} from 'axios'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useRefraModal} from '../../providers'
import {logger} from '../../utils'

interface DeleteRefractoryDocumentVariables {
  refractoryId: number
  documentId: number
}

export const useDeleteRefractoryDocument = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {closeModal} = useRefraModal()
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError<ErrorResponse>, DeleteRefractoryDocumentVariables>({
    mutationFn: async ({refractoryId, documentId}: DeleteRefractoryDocumentVariables) => {
      await ApiClient.axiosInstance.delete<void>(
        `/plants/refractory-product-data-catalog/refractories/${refractoryId}/documents/${documentId}`
      )
    },
    onError: (error) => {
      const errorDetails = error?.response?.data?.detail ?? error.message ?? error.response?.status
      logger
        .context('useDeleteRefractoryDocument')
        .extras({errorDetails})
        .error('Error occurred while deleting refractory document')

      spawnErrorToast(t('error.deleteRefractoryDocument'))
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === REFRACTORIES_WITH_CUSTOMIZATION_QUERY_KEY
      })
      spawnSuccessToast(t('success.deleteRefractoryDocument'))
    },
    onSettled: () => closeModal()
  })
}
